// Shared, Intellix-style analytics UI primitives: tinted stat tiles, compact
// breakdown bar-lists with country flags + percentages, and small helpers.
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid } from "recharts";

// ISO-2 country code -> emoji flag (regional indicator letters).
export const codeToFlag = (cc) => {
  if (!cc || typeof cc !== "string" || cc.length !== 2) return "\uD83C\uDF10"; // globe
  try {
    return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
  } catch {
    return "\uD83C\uDF10";
  }
};

export const fmtNum = (n) => new Intl.NumberFormat("en").format(Math.round(n || 0));

export const fmtDuration = (ms) => {
  if (ms == null) return "\u2013";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

// Tint palette mapped to the app's chart design tokens.
const TINTS = {
  blue: { v: "205 70% 55%" },
  green: { v: "152 45% 45%" },
  amber: { v: "38 92% 55%" },
  orange: { v: "29 99% 50%" },
  violet: { v: "280 40% 60%" },
  slate: { v: "240 12% 40%" },
};

export const StatTile = ({ icon: Icon, label, value, sub, tint = "slate", testid }) => {
  const c = (TINTS[tint] || TINTS.slate).v;
  return (
    <div
      className="rounded-xl border p-5"
      style={{ background: `hsl(${c} / 0.07)`, borderColor: `hsl(${c} / 0.22)` }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: `hsl(${c})` }}>{label}</span>
        {Icon && <Icon className="h-4 w-4" style={{ color: `hsl(${c})` }} />}
      </div>
      <p className="mt-2 font-mono text-3xl font-semibold tracking-tight text-foreground">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
};

// Compact daily trend bar chart (Humans vs Total/Events).
export const TrendChart = ({ data, keyA = "sessions", keyB = "events", labelA = "Sessions", labelB = "Events" }) => (
  <div className="h-44" data-testid="admin-analytics-chart">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data || []} barGap={2} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(d) => (d || "").slice(5)} />
        <Tooltip
          cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
          contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
        />
        <Bar dataKey={keyB} name={labelB} fill="hsl(var(--chart-3))" radius={[3, 3, 0, 0]} maxBarSize={22} />
        <Bar dataKey={keyA} name={labelA} fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

// A compact, scannable "Top X" list with a thin fill bar + share %.
// rows: [{ label, count, sessions?, code? }]
export const BreakdownList = ({ title, icon: Icon, rows, showFlag = false, labelHead = "Item", testid }) => {
  const data = rows || [];
  const max = Math.max(1, ...data.map((r) => r.count || 0));
  const total = data.reduce((s, r) => s + (r.count || 0), 0) || 1;
  return (
    <div className="rounded-xl border border-border bg-card" data-testid={testid || "admin-analytics-table"}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      </div>
      <div className="px-4 py-2">
        {data.length ? (
          <ul className="space-y-2.5 py-1.5">
            {data.map((r, i) => {
              const pct = Math.round(((r.count || 0) / total) * 100);
              const w = Math.max(3, Math.round(((r.count || 0) / max) * 100));
              return (
                <li key={i} className="group" data-testid="breakdown-row">
                  <div className="flex items-center gap-2 text-sm">
                    {showFlag && <span className="text-base leading-none">{codeToFlag(r.code)}</span>}
                    <span className="min-w-0 flex-1 truncate font-medium text-foreground" title={r.label}>{r.label || "\u2013"}</span>
                    <span className="font-mono text-xs tabular-nums text-foreground">{fmtNum(r.count)}</span>
                    <span className="w-10 text-right font-mono text-xs tabular-nums text-muted-foreground">{pct}%</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary/70 transition-all" style={{ width: `${w}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">No data yet</p>
        )}
      </div>
    </div>
  );
};
